import type { CourseSection } from "@brookbush/types";

import clsx from "clsx";
import { SparklesIcon, StarIcon } from "lucide-react";
import posthog from "posthog-js";
import { memo } from "react";

import type { CourseSectionProps } from "./CourseSection";
import CourseSectionHeaderControls from "./CourseSectionHeaderControls";

export interface CourseSectionHeaderProps extends CourseSectionProps {
	toggleIsExpanded: (section: CourseSection) => void;
}

const CourseSectionHeader = (props: CourseSectionHeaderProps) => {
	const {
		isCompleted,
		isVisited,
		isExpanded,

		isFreeSection,
		section,
		toggleIsExpanded,
		focusSections,
	} = props;

	const focusSectionData = focusSections?.find((s) => {
		if (section.parent) {
			return s.subSectionKey === section._key;
		}
		return s.sectionKey === section._key;
	});

	const isFocusSection = !!focusSectionData;

	return (
		<div
			onClick={() => {
				if (isFocusSection) {
					posthog.capture("exam_coach:recommended_section_opened");
				}
				toggleIsExpanded(section);
			}}
			className={clsx(
				"flex cursor-pointer items-center justify-between gap-4 phablet:gap-2 phablet:px-5 px-4 phablet:py-3 py-2 hover:brightness-95 dark:hover:brightness-75",
				isFocusSection
					? "bg-purple-50/60 dark:bg-purple-500/10"
					: isFreeSection
						? "bg-slate-50 dark:bg-slate-700"
						: isCompleted
							? "bg-emerald-50/60 dark:bg-emerald-300/10"
							: section.parent
								? "bg-amber-50/50 dark:bg-amber-300/10"
								: "bg-sky-50/50 dark:bg-sky-300/10",

				isFocusSection
					? "border-purple-500/50 dark:border-purple-400/40"
					: isFreeSection
						? "border-slate-300 dark:border-slate-500"
						: isCompleted
							? "border-emerald-500/50 dark:border-emerald-300/40"
							: section.parent
								? "border-amber-700/20 dark:border-amber-300/40"
								: "border-sky-700/30 dark:border-sky-200/30",

				isExpanded && section.body?.length
					? "rounded-t-xl border-2 border-b-0"
					: "rounded-xl border-2",

				{
					"hue-rotate-30":
						isVisited && !isCompleted && !isFreeSection && !isFocusSection,
				},
			)}
		>
			<h2
				id={`${section._key}-title`}
				className={
					"flex select-none laptop:flex-row flex-col laptop:items-center gap-2 laptop:gap-3 font-semibold text-lg transition-all sm:text-[1.4rem]"
				}
				key={`${section._key}-title`}
			>
				<div>
					{(section as CourseSection).isCritical && (
						<StarIcon
							id="test-critical-content-icon"
							className={clsx(
								"relative bottom-0.5 -left-1 mr-0.5 phablet:mr-1 inline-block h-4 phablet:h-5 phablet:w-5 w-4 rounded-full fill-orange-400 text-orange-400",
								{
									"-hue-rotate-30":
										isVisited && !isCompleted && !isFocusSection,
								},
							)}
						/>
					)}

					{isFocusSection && (
						<div
							className="tooltip tooltip-right before:max-w-xs before:whitespace-normal"
							onMouseEnter={() => {
								posthog.capture(
									"exam_coach:recommended_section_tooltip_viewed",
								);
							}}
							data-tip={focusSectionData?.rationale || "Recommended study area"}
						>
							<SparklesIcon
								className={clsx(
									"relative bottom-0.5 -left-1 mr-0.5 phablet:mr-1 inline-block h-4 phablet:h-5 phablet:w-5 w-4 rounded-full fill-purple-400 text-purple-400",
								)}
							/>
						</div>
					)}

					{section.title}
				</div>

				{!!section.subSections?.length && (
					<span
						className={clsx(
							"relative mb-1.5 phablet:mb-0 block max-w-max shrink-0 items-center rounded-full border-[1.5px] phablet:px-2.5 px-2.5 phablet:py-0.5 py-1 font-medium phablet:text-sm text-slate-700 text-xs dark:text-white",
							isFocusSection
								? "border-purple-500/50 bg-purple-50 dark:border-purple-400/40 dark:bg-purple-500/10"
								: isCompleted
									? "border-emerald-500/50 bg-emerald-50 dark:border-emerald-300/40 dark:bg-emerald-300/10"
									: "border-amber-700/20 bg-amber-50 dark:border-amber-300/30 dark:bg-amber-300/20",
							{
								"-hue-rotate-30": isVisited && !isCompleted && !isFocusSection,
							},
						)}
					>
						{section.subSections?.length} Sub Section
						{section.subSections?.length === 1 ? "" : "s"}
					</span>
				)}
			</h2>

			<CourseSectionHeaderControls {...props} />
		</div>
	);
};

export default memo(CourseSectionHeader);
